import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { meetsTier } from '../utils/premiumTier';

/* ── Seeded random helpers ────────────────────────────────────── */

function seededRandom(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  return () => {
    h = (h * 16807 + 0) % 2147483647;
    return (h & 0x7fffffff) / 2147483647;
  };
}

function pickDailyStories(accountId, storiesPosts, min = 2, max = 3) {
  if (!storiesPosts || !storiesPosts.length || max <= 0) return [];
  const dateStr = new Date().toISOString().slice(0, 10);
  const rng = seededRandom(accountId + dateStr);
  const lo = Math.max(0, min);
  const hi = Math.max(lo, max);
  const count = lo === hi ? lo : lo + Math.floor(rng() * (hi - lo + 1));
  if (count <= 0) return [];
  const shuffled = [...storiesPosts].sort(() => rng() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

/* ── SVG icons ────────────────────────────────────────────────── */

function BackArrowIcon({ size = 24, color = '#262626' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function HeartIcon({ size = 20, filled = false }) {
  return filled ? (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#ed4956" stroke="none">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function GridIcon({ active }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={active ? '#262626' : '#8e8e8e'} strokeWidth="1.5">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}

function LockIcon({ size = 20, color = '#8e8e8e' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function CloseIcon({ size = 24, color = '#fff' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/* ── Story avatar ring ────────────────────────────────────────── */

const STORY_GRADIENT_ID = 'insta-story-gradient';

function StoryAvatar({ src, displayName, size = 56, hasUnviewed, onClick }) {
  const ringSize = size + 10;
  const ringThickness = 3;
  const gap = 3;
  const radius = (ringSize - ringThickness) / 2;

  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center flex-shrink-0"
      style={{ width: size + 16 }}
    >
      <div className="relative" style={{ width: ringSize, height: ringSize }}>
        <svg width={ringSize} height={ringSize} className="absolute inset-0">
          <defs>
            <linearGradient id={STORY_GRADIENT_ID} x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#feda75" />
              <stop offset="25%" stopColor="#fa7e1e" />
              <stop offset="50%" stopColor="#d62976" />
              <stop offset="75%" stopColor="#962fbf" />
              <stop offset="100%" stopColor="#4f5bd5" />
            </linearGradient>
          </defs>
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={radius}
            fill="none"
            strokeWidth={ringThickness}
            stroke={hasUnviewed ? `url(#${STORY_GRADIENT_ID})` : '#c7c7c7'}
          />
        </svg>
        <div
          className="absolute rounded-full overflow-hidden bg-gray-200"
          style={{
            top: ringThickness + gap,
            left: ringThickness + gap,
            width: size - gap * 2 + 4,
            height: size - gap * 2 + 4,
          }}
        >
          <img
            src={src}
            alt={displayName}
            className="w-full h-full object-cover"
            draggable={false}
          />
        </div>
      </div>
      <span
        className="text-xs mt-1 truncate w-full text-center"
        style={{ color: '#262626', fontSize: 11 }}
      >
        {displayName?.split(' ')[0] || 'User'}
      </span>
    </button>
  );
}

/* ── Top bar ──────────────────────────────────────────────────── */

function TopBar({ title, onBack }) {
  return (
    <div
      className="flex items-center px-3 py-2.5 flex-shrink-0"
      style={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #dbdbdb',
        minHeight: 44,
      }}
    >
      <button onClick={onBack} className="p-1 -ml-1 mr-2 flex-shrink-0">
        <BackArrowIcon />
      </button>
      <h1
        className="flex-1 text-center font-semibold truncate pr-8"
        style={{ color: '#262626', fontSize: 16 }}
      >
        {title}
      </h1>
    </div>
  );
}

/* ── Account list item ────────────────────────────────────────── */

function AccountCard({ account, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center w-full px-4 py-3 text-left"
      style={{ borderBottom: '1px solid #efefef' }}
    >
      <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
        <img
          src={account.avatar?.url128 || account.avatar?.url64}
          alt={account.displayName}
          className="w-full h-full object-cover"
          draggable={false}
        />
      </div>
      <div className="ml-3 min-w-0 flex-1">
        <div className="font-semibold text-sm truncate" style={{ color: '#262626' }}>
          {account.displayName}
        </div>
        <div className="text-sm truncate" style={{ color: '#8e8e8e' }}>
          @{account.username}
        </div>
      </div>
    </button>
  );
}

/* ── Post grid thumbnail ──────────────────────────────────────── */

function PostThumb({ post, onClick }) {
  return (
    <button onClick={onClick} className="relative aspect-square w-full overflow-hidden bg-gray-100 group">
      <img
        src={post.imagePreview512 || post.imageUrl}
        alt=""
        className="w-full h-full object-cover transition-opacity duration-150 group-hover:opacity-80"
        loading="lazy"
        draggable={false}
      />
    </button>
  );
}

/* ── Feed post ────────────────────────────────────────────────── */

function FeedPost({ post, account }) {
  const [liked, setLiked] = useState(false);
  const [showHeart, setShowHeart] = useState(false);

  const handleDoubleTap = useCallback(() => {
    setLiked(true);
    setShowHeart(true);
  }, []);

  return (
    <div style={{ borderBottom: '1px solid #efefef' }}>
      {/* Header */}
      <div className="flex items-center px-3 py-2.5">
        <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
          <img
            src={account.avatar?.url64 || account.avatar?.url128}
            alt={account.displayName}
            className="w-full h-full object-cover"
          />
        </div>
        <span className="ml-2.5 font-semibold text-sm" style={{ color: '#262626' }}>
          {account.username}
        </span>
      </div>

      {/* Image */}
      <div className="w-full relative overflow-hidden" onDoubleClick={handleDoubleTap}>
        <img
          src={post.imageUrl || post.imagePreview512}
          alt=""
          className="w-full"
          style={{ display: 'block' }}
          draggable={false}
        />
        <AnimatePresence>
          {showHeart && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              onAnimationComplete={() => setTimeout(() => setShowHeart(false), 600)}
            >
              <motion.svg
                viewBox="0 0 24 24"
                fill="white"
                className="drop-shadow-lg"
                style={{ width: 80, height: 80, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))' }}
                initial={{ scale: 0, opacity: 0.9 }}
                animate={{ scale: [0, 1.3, 1], opacity: [0.9, 1, 0] }}
                transition={{ duration: 0.8, times: [0, 0.3, 1], ease: 'easeOut' }}
              >
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </motion.svg>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Actions + Likes */}
      <div className="flex items-center gap-1.5 px-3 py-2">
        <button onClick={() => setLiked((v) => !v)} className="p-1 -ml-1">
          <motion.div animate={liked ? { scale: [1, 1.3, 1] } : {}} transition={{ duration: 0.3 }}>
            <HeartIcon filled={liked} />
          </motion.div>
        </button>
        <span className="font-semibold text-sm" style={{ color: '#262626' }}>
          {((post.likes || 0) + (liked ? 1 : 0)).toLocaleString()} likes
        </span>
      </div>

      {/* Caption */}
      {post.caption && (
        <div className="px-3 pb-3 pt-1">
          <span className="font-semibold text-sm mr-1" style={{ color: '#262626' }}>
            {account.username}
          </span>
          <span className="text-sm" style={{ color: '#262626' }}>
            {post.caption}
          </span>
        </div>
      )}
    </div>
  );
}

/* ── Story overlay ────────────────────────────────────────────── */

function StoryOverlay({ story, account, onClose }) {
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setTimeout(onClose, 6000);
    return () => clearTimeout(timerRef.current);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: '#000' }}
      onClick={onClose}
    >
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 z-10 px-2 pt-2">
        <div className="h-0.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}>
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: '#fff' }}
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 6, ease: 'linear' }}
          />
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center px-3 pt-5 pb-2 z-10">
        <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
          <img
            src={account.avatar?.url64 || account.avatar?.url128}
            alt={account.displayName}
            className="w-full h-full object-cover"
          />
        </div>
        <span className="ml-2 text-sm font-semibold text-white">{account.username}</span>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="ml-auto p-1"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Story image */}
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        <img
          src={story.imageUrl || story.imagePreview512}
          alt=""
          className="max-w-full max-h-full object-contain"
          draggable={false}
        />
      </div>
    </motion.div>
  );
}

/* ── Feed view overlay ────────────────────────────────────────── */

function FeedView({ posts, account, startIndex, onClose }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    // Scroll to the clicked post on mount
    if (scrollRef.current && startIndex > 0) {
      requestAnimationFrame(() => {
        const el = scrollRef.current?.children[startIndex];
        if (el) {
          scrollRef.current.scrollTop = el.offsetTop;
        }
      });
    }
  }, [startIndex]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 30 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      className="absolute inset-0 z-40 flex flex-col"
      style={{ backgroundColor: '#ffffff', touchAction: 'pan-y' }}
    >
      {/* Top bar */}
      <div
        className="flex items-center px-3 py-2.5 flex-shrink-0"
        style={{ borderBottom: '1px solid #dbdbdb', minHeight: 44 }}
      >
        <button onClick={onClose} className="p-1 -ml-1 mr-2 flex-shrink-0">
          <BackArrowIcon />
        </button>
        <h2 className="font-semibold text-base" style={{ color: '#262626' }}>
          Posts
        </h2>
      </div>

      {/* Scrollable feed */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        {posts.map((post, i) => (
          <FeedPost key={post.id || i} post={post} account={account} />
        ))}
      </div>
    </motion.div>
  );
}

/* ── Profile page ─────────────────────────────────────────────── */

function ProfileAvatar({ account, hasStories, hasUnviewedStories, onStoryClick }) {
  const size = 80;
  if (!hasStories) {
    return (
      <div className="rounded-full overflow-hidden bg-gray-200 flex-shrink-0" style={{ width: size, height: size }}>
        <img src={account.avatar?.url128 || account.avatar?.url64} alt="" className="w-full h-full object-cover" draggable={false} />
      </div>
    );
  }
  const ringSize = size + 10;
  const ringThickness = 3;
  const radius = (ringSize - ringThickness) / 2;
  const hasUnviewed = hasUnviewedStories !== false;
  return (
    <button onClick={onStoryClick} className="flex-shrink-0" style={{ width: ringSize, height: ringSize, position: 'relative' }}>
      <svg width={ringSize} height={ringSize} style={{ position: 'absolute', inset: 0 }}>
        <defs>
          <linearGradient id="profile-story-grad" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor={hasUnviewed ? '#FCAF45' : '#dbdbdb'} />
            <stop offset="50%" stopColor={hasUnviewed ? '#E1306C' : '#dbdbdb'} />
            <stop offset="100%" stopColor={hasUnviewed ? '#833AB4' : '#dbdbdb'} />
          </linearGradient>
        </defs>
        <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="url(#profile-story-grad)" strokeWidth={ringThickness} />
      </svg>
      <div className="absolute rounded-full overflow-hidden bg-white" style={{ top: 5, left: 5, width: size, height: size, padding: 2 }}>
        <img src={account.avatar?.url128 || account.avatar?.url64} alt="" className="w-full h-full object-cover rounded-full" draggable={false} />
      </div>
    </button>
  );
}

function ProfilePage({ account, instaPosts, playerTier, patreonTiers, instaPrivateTier, onBack, onNavigatePatreon, hasStories, hasUnviewedStories, onStoryClick, t }) {
  const [activeTab, setActiveTab] = useState('public');
  const [feedView, setFeedView] = useState(null); // { posts, startIndex }

  // Merge account posts with dynamic instaPosts for this character
  // Dynamic (node) posts always go on top (newest), base posts keep admin order
  const allPosts = useMemo(() => {
    const base = account.posts || [];
    const dynamic = (instaPosts || []).filter(
      (p) => p.characterId === account.characterId
    );
    const ids = new Set(base.map((p) => p.id));
    const uniqueDynamic = dynamic.filter((p) => !ids.has(p.id));
    // Dynamic first (newest on top), then base in admin order
    return [...uniqueDynamic.reverse(), ...base];
  }, [account, instaPosts]);

  const publicPosts = useMemo(
    () => allPosts.filter((p) => p.section === 'public'),
    [allPosts]
  );

  const privatePosts = useMemo(
    () => allPosts.filter((p) => p.section === 'private'),
    [allPosts]
  );

  const canSeePremium = useMemo(
    () => meetsTier(playerTier, instaPrivateTier, patreonTiers),
    [playerTier, instaPrivateTier, patreonTiers]
  );

  const currentPosts = activeTab === 'public' ? publicPosts : privatePosts;

  const openFeed = useCallback(
    (index) => {
      setFeedView({ posts: currentPosts, startIndex: index });
    },
    [currentPosts]
  );

  return (
    <div className="flex flex-col h-full relative" style={{ backgroundColor: '#ffffff' }}>
      {/* Top bar */}
      <TopBar title={`@${account.username}`} onBack={onBack} />

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto overscroll-contain" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
        {/* Profile header */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center">
            <ProfileAvatar account={account} hasStories={hasStories} hasUnviewedStories={hasUnviewedStories} onStoryClick={onStoryClick} />
            <div className="ml-5 flex-1">
              <div className="flex gap-6 justify-center text-center">
                <div>
                  <div className="font-semibold text-base" style={{ color: '#262626' }}>
                    {publicPosts.length + privatePosts.length}
                  </div>
                  <div className="text-xs" style={{ color: '#8e8e8e' }}>
                    {t?.('posts') || 'Posts'}
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-base" style={{ color: '#262626' }}>
                    {((account.username || '').length * 847) % 2500 + 300}
                  </div>
                  <div className="text-xs" style={{ color: '#8e8e8e' }}>
                    {t?.('followers') || 'Followers'}
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-base" style={{ color: '#262626' }}>
                    {((account.username || '').length * 137) % 500 + 100}
                  </div>
                  <div className="text-xs" style={{ color: '#8e8e8e' }}>
                    {t?.('following') || 'Following'}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <div className="font-semibold text-sm" style={{ color: '#262626' }}>
              {account.displayName}
            </div>
            {account.bio && (
              <div className="text-sm mt-0.5 whitespace-pre-line" style={{ color: '#262626' }}>
                {account.bio}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div
          className="flex mt-2"
          style={{ borderTop: '1px solid #dbdbdb' }}
        >
          <button
            className="flex-1 flex justify-center items-center py-2.5 transition-colors"
            style={{
              borderBottom: activeTab === 'public' ? '2px solid #262626' : '2px solid transparent',
            }}
            onClick={() => setActiveTab('public')}
          >
            <GridIcon active={activeTab === 'public'} />
            <span
              className="ml-1.5 text-xs font-semibold uppercase tracking-wider"
              style={{ color: activeTab === 'public' ? '#262626' : '#8e8e8e' }}
            >
              {t?.('public') || 'Public'}
            </span>
          </button>
          <button
            className="flex-1 flex justify-center items-center py-2.5 transition-colors"
            style={{
              borderBottom: activeTab === 'private' ? '2px solid #262626' : '2px solid transparent',
            }}
            onClick={() => setActiveTab('private')}
          >
            <LockIcon size={18} color={activeTab === 'private' ? '#262626' : '#8e8e8e'} />
            <span
              className="ml-1.5 text-xs font-semibold uppercase tracking-wider"
              style={{ color: activeTab === 'private' ? '#262626' : '#8e8e8e' }}
            >
              {t?.('private') || 'Private'}
            </span>
          </button>
        </div>

        {/* Content area */}
        {activeTab === 'private' && !canSeePremium ? (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ border: '2px solid #dbdbdb' }}
            >
              <LockIcon size={32} color="#8e8e8e" />
            </div>
            <div className="font-semibold text-base mb-1" style={{ color: '#262626' }}>
              Private Content
            </div>
            <div className="text-sm text-center mb-4" style={{ color: '#8e8e8e' }}>
              {instaPrivateTier
                ? `Unlock with ${instaPrivateTier} tier or higher`
                : 'Subscribe to unlock exclusive private posts'}
            </div>
            {onNavigatePatreon && (
              <button
                onClick={onNavigatePatreon}
                className="px-6 py-2 rounded-lg font-semibold text-sm text-white"
                style={{ backgroundColor: '#0095f6' }}
              >
                {instaPrivateTier ? `Get ${instaPrivateTier}` : 'Subscribe'}
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-px" style={{ backgroundColor: '#ffffff' }}>
            {currentPosts.map((post, i) => (
              <PostThumb key={post.id || i} post={post} onClick={() => openFeed(i)} />
            ))}
            {currentPosts.length === 0 && (
              <div className="col-span-3 py-16 text-center text-sm" style={{ color: '#8e8e8e', backgroundColor: '#ffffff' }}>
                {t?.('noPosts') || 'No posts yet'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Feed view overlay */}
      <AnimatePresence>
        {feedView && (
          <FeedView
            posts={feedView.posts}
            account={account}
            startIndex={feedView.startIndex}
            onClose={() => setFeedView(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Main InstaApp component ──────────────────────────────────── */

export default function InstaApp({
  instaAccounts,
  instaAppName,
  instaPrivateTier,
  storiesMin = 2,
  storiesMax = 3,
  unlockedContacts,
  playerTier,
  patreonTiers,
  instaPosts,
  viewedStories,
  onViewStory,
  onBack,
  onNavigatePatreon,
  t,
}) {
  const [page, setPage] = useState('list'); // 'list' | 'profile'
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [storyOverlay, setStoryOverlay] = useState(null); // { story, account }

  // Filter accounts to only those whose character is unlocked
  const visibleAccounts = useMemo(
    () =>
      (instaAccounts || []).filter(
        (acc) => unlockedContacts && unlockedContacts[acc.characterId]
      ),
    [instaAccounts, unlockedContacts]
  );

  const canSeePremium = useMemo(
    () => meetsTier(playerTier, instaPrivateTier, patreonTiers),
    [playerTier, instaPrivateTier, patreonTiers]
  );

  // Build stories data for all visible accounts
  const accountStories = useMemo(() => {
    const map = {};
    for (const acc of visibleAccounts) {
      // Stories come from account posts with section === 'stories'
      // Also include dynamic instaPosts with section === 'stories'
      const basePosts = (acc.posts || []).filter((p) => p.section === 'stories');
      const dynamicPosts = (instaPosts || [])
        .filter((p) => p.characterId === acc.characterId && p.section === 'stories');

      const allStoryPosts = [...basePosts];
      const ids = new Set(basePosts.map((p) => p.id));
      for (const p of dynamicPosts) {
        if (!ids.has(p.id)) {
          allStoryPosts.push(p);
          ids.add(p.id);
        }
      }

      const daily = pickDailyStories(acc.id, allStoryPosts, storiesMin, storiesMax);
      if (daily.length > 0) {
        map[acc.id] = daily;
      }
    }
    return map;
  }, [visibleAccounts, instaPosts]);

  // Accounts that have stories today
  const accountsWithStories = useMemo(
    () => visibleAccounts.filter((acc) => accountStories[acc.id]?.length > 0),
    [visibleAccounts, accountStories]
  );

  const openAccount = useCallback((acc) => {
    setSelectedAccount(acc);
    setPage('profile');
  }, []);

  const goBackToList = useCallback(() => {
    setPage('list');
    setSelectedAccount(null);
  }, []);

  const [tierToast, setTierToast] = useState(false);

  const openStory = useCallback(
    (acc) => {
      if (!canSeePremium) {
        setTierToast(true);
        setTimeout(() => setTierToast(false), 2500);
        return;
      }
      const stories = accountStories[acc.id];
      if (!stories || stories.length === 0) return;

      // Find first unviewed story, or show the first one
      const viewed = viewedStories?.[acc.id] || [];
      const unviewed = stories.find((s) => !viewed.includes(s.id));
      const story = unviewed || stories[0];

      setStoryOverlay({ story, account: acc });
    },
    [accountStories, viewedStories, canSeePremium, onNavigatePatreon]
  );

  const closeStory = useCallback(() => {
    if (storyOverlay && onViewStory) {
      onViewStory(storyOverlay.account.id, storyOverlay.story.id);
    }
    setStoryOverlay(null);
  }, [storyOverlay, onViewStory]);

  const hasUnviewedStories = useCallback(
    (accId) => {
      const stories = accountStories[accId];
      if (!stories || stories.length === 0) return false;
      const viewed = viewedStories?.[accId] || [];
      return stories.some((s) => !viewed.includes(s.id));
    },
    [accountStories, viewedStories]
  );

  const appTitle = instaAppName || 'Instagram';

  return (
    <div
      className="flex flex-col h-full overflow-hidden pt-6"
      style={{
        backgroundColor: '#ffffff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <AnimatePresence mode="popLayout">
        {page === 'list' && (
          <motion.div
            key="list"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="flex flex-col h-full"
          >
            {/* Top bar */}
            <TopBar title={appTitle} onBack={onBack} />

            <div className="flex-1 overflow-y-auto overscroll-contain" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
              {/* Stories row */}
              {accountsWithStories.length > 0 && (
                <div
                  className="py-2.5 overflow-x-auto flex-shrink-0"
                  style={{ borderBottom: '1px solid #efefef' }}
                >
                  <div className="flex gap-2 px-3" style={{ minWidth: 'min-content' }}>
                    {accountsWithStories.map((acc) => (
                      <StoryAvatar
                        key={acc.id}
                        src={acc.avatar?.url128 || acc.avatar?.url64}
                        displayName={acc.displayName}
                        hasUnviewed={hasUnviewedStories(acc.id)}
                        onClick={() => openStory(acc)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Account list */}
              <div>
                {visibleAccounts.map((acc) => (
                  <AccountCard
                    key={acc.id}
                    account={acc}
                    onClick={() => openAccount(acc)}
                  />
                ))}
                {visibleAccounts.length === 0 && (
                  <div className="py-16 text-center text-sm" style={{ color: '#8e8e8e' }}>
                    {t?.('noAccounts') || 'No accounts yet'}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {page === 'profile' && selectedAccount && (
          <motion.div
            key="profile"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="flex flex-col h-full"
          >
            <ProfilePage
              account={selectedAccount}
              instaPosts={instaPosts}
              playerTier={playerTier}
              patreonTiers={patreonTiers}
              instaPrivateTier={instaPrivateTier}
              onBack={goBackToList}
              onNavigatePatreon={onNavigatePatreon}
              hasStories={!!(accountStories[selectedAccount?.id]?.length)}
              hasUnviewedStories={!!(accountStories[selectedAccount?.id]?.some((s) => !(viewedStories?.[selectedAccount?.id] || []).includes(s.id)))}
              onStoryClick={() => selectedAccount && openStory(selectedAccount)}
              t={t}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Story overlay */}
      <AnimatePresence>
        {storyOverlay && (
          <StoryOverlay
            story={storyOverlay.story}
            account={storyOverlay.account}
            onClose={closeStory}
          />
        )}
      </AnimatePresence>

      {/* Tier toast for locked content */}
      <AnimatePresence>
        {tierToast && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-6 left-4 right-4 z-50 flex items-center justify-center"
          >
            <div className="rounded-xl px-4 py-3 shadow-lg text-center" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}>
              <div className="text-white text-sm font-semibold mb-0.5">
                {instaPrivateTier ? `${instaPrivateTier} tier required` : 'Premium content'}
              </div>
              <div className="text-white/60 text-xs">
                Stories and private posts require a subscription
              </div>
              {onNavigatePatreon && (
                <button
                  onClick={() => { setTierToast(false); onNavigatePatreon(); }}
                  className="mt-2 text-xs font-semibold"
                  style={{ color: '#0095f6' }}
                >
                  Learn more
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
